/**
 * M29 task-05 — the `Track settings` card on the Track edit page (HUB-085 D1/D2,
 * COP-118 D1). Five keys of `tf_track_setting` become operator-visible: a token
 * limit, the per-track model, and the three planner budgets grouped under a
 * `Planner` subheading INSIDE the same card and under the same single Save —
 * they are rows in the same table written through the same endpoint, and a
 * second card would imply otherwise (COP-118 D1).
 *
 * The card is pure/controlled so the no-jsdom seam test can render it via
 * `renderToStaticMarkup`; `TracksEdit` owns the fetching and the calls.
 */
import { TrackSettingDefaults, TrackSettingValueSchemas, type LlmModel, type TrackSettingKey } from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

/** Every field as the operator typed it — an `<input type="number">` yields a string. */
export interface TrackSettingsFormValues {
  tokenLimit: string;
  modelId: string;
  plannerModelId: string;
  plannerWallTimeMs: string;
  plannerReadCallLimit: string;
}

export const EMPTY_TRACK_SETTINGS: TrackSettingsFormValues = {
  tokenLimit: '',
  modelId: '',
  plannerModelId: '',
  plannerWallTimeMs: '',
  plannerReadCallLimit: '',
};

/**
 * The three integer keys, pointed straight at the shared
 * `TrackSettingValueSchemas` single-home — the bounds are NOT hand-copied here,
 * so a change to COP-118 D3's numbers moves the form, the `min`/`max`
 * attributes and the validation message together.
 */
const NUMERIC_SCHEMAS = {
  token_limit: TrackSettingValueSchemas.token_limit,
  planner_wall_time_ms: TrackSettingValueSchemas.planner_wall_time_ms,
  planner_read_call_limit: TrackSettingValueSchemas.planner_read_call_limit,
} as const;

/**
 * The unset defaults the hub falls back to, shown as placeholders (COP-118 D1)
 * — read from `TrackSettingDefaults` for the same reason the bounds above are,
 * and learned the same way the bounds were not: these were hand-copied, PLN-04
 * raised the planner wall time to 120s in `planner-subagent.ts`, and this card
 * kept advertising 30s.
 */
const PLANNER_WALL_TIME_DEFAULT = String(TrackSettingDefaults.planner_wall_time_ms);
const PLANNER_READ_CALL_LIMIT_DEFAULT = String(TrackSettingDefaults.planner_read_call_limit);

/**
 * Field order for both the rendered form and the save plan. Pinned against
 * `TrackSettingValueSchemas` in the tests so a sixth key cannot be smuggled in
 * on one side only.
 */
export const TRACK_SETTING_FIELDS: readonly {
  key: TrackSettingKey;
  field: keyof TrackSettingsFormValues;
  label: string;
}[] = [
  { key: 'token_limit', field: 'tokenLimit', label: 'Token limit' },
  { key: 'model', field: 'modelId', label: 'Model' },
  { key: 'planner_model', field: 'plannerModelId', label: 'Planner model' },
  { key: 'planner_wall_time_ms', field: 'plannerWallTimeMs', label: 'Planner wall time (ms)' },
  { key: 'planner_read_call_limit', field: 'plannerReadCallLimit', label: 'Planner read-call limit' },
];

export type TrackSettingOp =
  | { op: 'put'; key: TrackSettingKey; value: string | number }
  | { op: 'delete'; key: TrackSettingKey };

export interface TrackSettingsSavePlan {
  /** The calls Save issues, in `TRACK_SETTING_FIELDS` order. */
  ops: TrackSettingOp[];
  /** Form-level validation failures; the offending field contributes no op. */
  errors: string[];
}

const boundsMessage = (label: string, minimum?: number, maximum?: number): string => {
  if (minimum !== undefined && maximum !== undefined)
    return `${label} must be a whole number between ${minimum} and ${maximum}.`;
  if (minimum !== undefined) return `${label} must be a whole number of at least ${minimum}.`;
  return `${label} must be a whole number of at most ${maximum}.`;
};

/**
 * The exact calls a Save issues (exported pure seam — HUB-085 D2's
 * "upsert when set, delete when cleared", per field, under one button).
 *
 * Order is stable and documented: `token_limit`, `model`, `planner_model`,
 * `planner_wall_time_ms`, `planner_read_call_limit` — the declaration order of
 * `TrackSettingValueSchemas`.
 *
 * An empty value is a DELETE in every row: a placeholder is not a value and is
 * never sent. Out-of-range or non-integer numbers produce a form-level error
 * and no op for that field, so one bad entry cannot cancel the other four.
 */
export function buildSavePlan(values: TrackSettingsFormValues): TrackSettingsSavePlan {
  const ops: TrackSettingOp[] = [];
  const errors: string[] = [];

  for (const { key, field, label } of TRACK_SETTING_FIELDS) {
    const raw = values[field].trim();
    if (raw === '') {
      ops.push({ op: 'delete', key });
      continue;
    }
    const schema = NUMERIC_SCHEMAS[key as keyof typeof NUMERIC_SCHEMAS] as
      | { minimum?: number; maximum?: number }
      | undefined;
    // `model` / `planner_model` hold a registry uuid picked from the dropdown —
    // no client-side range to check; the hub's existence check is authoritative.
    if (!schema) {
      ops.push({ op: 'put', key, value: raw });
      continue;
    }
    const n = Number(raw);
    const { minimum, maximum } = schema;
    const outOfRange =
      !Number.isInteger(n) ||
      (minimum !== undefined && n < minimum) ||
      (maximum !== undefined && n > maximum);
    if (outOfRange) {
      errors.push(boundsMessage(label, minimum, maximum));
      continue;
    }
    ops.push({ op: 'put', key, value: n });
  }

  return { ops, errors };
}

/** The stored rows as the form shows them — a missing key renders empty, never `0`. */
export function settingsFromRows(rows: readonly { key: string; value: unknown }[]): TrackSettingsFormValues {
  const byKey = new Map(rows.map((r) => [r.key, r.value]));
  const asText = (key: TrackSettingKey): string => {
    const value = byKey.get(key);
    return value === undefined || value === null ? '' : String(value);
  };
  return {
    tokenLimit: asText('token_limit'),
    modelId: asText('model'),
    plannerModelId: asText('planner_model'),
    plannerWallTimeMs: asText('planner_wall_time_ms'),
    plannerReadCallLimit: asText('planner_read_call_limit'),
  };
}

export interface TrackSettingsFormProps {
  models: LlmModel[];
  /** True until BOTH the settings list and the model list have resolved. */
  loading: boolean;
  tokenLimit: string;
  modelId: string;
  plannerModelId: string;
  plannerWallTimeMs: string;
  plannerReadCallLimit: string;
  onTokenLimit(v: string): void;
  onModelId(v: string): void;
  onPlannerModelId(v: string): void;
  onPlannerWallTimeMs(v: string): void;
  onPlannerReadCallLimit(v: string): void;
  onSave(): void;
  saving: boolean;
  error: string | null;
}

interface ModelSelectProps {
  id: string;
  label: string;
  models: LlmModel[];
  value: string;
  onChange: (v: string) => void;
}

/**
 * A registry-model dropdown. The empty option is a MEANINGFUL action — picking
 * it deletes the override — so it is labelled explicitly and is NOT `disabled`
 * (HUB-085 D2). With an empty registry it is the only option, and it says so.
 */
function ModelSelect({ id, label, models, value, onChange }: ModelSelectProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select
        id={id}
        aria-label={label}
        className="w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">
          {models.length === 0 ? 'No models registered — using global assignment' : 'Use global assignment'}
        </option>
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {m.internalName} ({m.provider}:{m.modelName})
          </option>
        ))}
      </Select>
    </div>
  );
}

interface NumberFieldProps {
  id: string;
  label: string;
  hint: string;
  placeholder?: string;
  bounds: { minimum?: number; maximum?: number };
  value: string;
  onChange: (v: string) => void;
}

function NumberField({ id, label, hint, placeholder, bounds, value, onChange }: NumberFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        step={1}
        min={bounds.minimum}
        max={bounds.maximum}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

/** The `Track settings` card (exported seam) — all five fields, one Save. */
export function TrackSettingsForm(props: TrackSettingsFormProps) {
  const {
    models,
    loading,
    tokenLimit,
    modelId,
    plannerModelId,
    plannerWallTimeMs,
    plannerReadCallLimit,
    onTokenLimit,
    onModelId,
    onPlannerModelId,
    onPlannerWallTimeMs,
    onPlannerReadCallLimit,
    onSave,
    saving,
    error,
  } = props;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Track settings</CardTitle>
        <CardDescription>
          Per-track overrides. Clearing a field removes the override and falls back to the global configuration.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* "No override" and "not yet loaded" must not render identically
            (HUB-085 D2), so the fields appear only once both lists resolve. */}
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading track settings…</p>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              onSave();
            }}
          >
            {error && <Alert variant="destructive">{error}</Alert>}
            <NumberField
              id="tokenLimit"
              label="Token limit"
              hint="Empty means no per-track cap."
              bounds={NUMERIC_SCHEMAS.token_limit}
              value={tokenLimit}
              onChange={onTokenLimit}
            />
            <ModelSelect id="trackModel" label="Model" models={models} value={modelId} onChange={onModelId} />
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-medium">Planner</h3>
              <ModelSelect
                id="plannerModel"
                label="Planner model"
                models={models}
                value={plannerModelId}
                onChange={onPlannerModelId}
              />
              <NumberField
                id="plannerWallTimeMs"
                label="Planner wall time (ms)"
                hint="Empty uses the default."
                placeholder={PLANNER_WALL_TIME_DEFAULT}
                bounds={NUMERIC_SCHEMAS.planner_wall_time_ms}
                value={plannerWallTimeMs}
                onChange={onPlannerWallTimeMs}
              />
              <NumberField
                id="plannerReadCallLimit"
                label="Planner read-call limit"
                hint="Empty uses the default."
                placeholder={PLANNER_READ_CALL_LIMIT_DEFAULT}
                bounds={NUMERIC_SCHEMAS.planner_read_call_limit}
                value={plannerReadCallLimit}
                onChange={onPlannerReadCallLimit}
              />
            </div>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
